import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useAnchoredPosition } from "hooks/useAnchoredPosition";
import { TooltipSurface } from "components/common/TooltipSurface";
import { resolveTooltipAnchoring } from "components/common/TooltipSurface/resolveTooltipAnchoring";
import type { TooltipPosition, TooltipProps } from "./types";

const TOOLTIP_MAX_WIDTH = 360;
const VIEWPORT_MARGIN = 8;

export const Tooltip = ({ label, children }: TooltipProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLSpanElement>(null);

  const [isHovered, setIsHovered] = useState(false);
  const [surfaceWidth, setSurfaceWidth] = useState<number | null>(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const position = useAnchoredPosition<TooltipPosition>({
    isOpen: isHovered,
    anchorRef,
    compute: (anchorRect) => resolveTooltipAnchoring({ anchorRect }),
  });

  // Center the surface on the anchor, clamped to the viewport; aim the arrow at the anchor.
  const isMeasured = surfaceWidth !== null;
  const viewportLeft =
    position === null || surfaceWidth === null
      ? VIEWPORT_MARGIN
      : Math.max(
          VIEWPORT_MARGIN,
          Math.min(
            position.anchorCenterX - surfaceWidth / 2,
            window.innerWidth - VIEWPORT_MARGIN - surfaceWidth,
          ),
        );
  const arrowOffsetLeft =
    position === null || surfaceWidth === null
      ? undefined
      : position.anchorCenterX - viewportLeft;

  useEffect(() => {
    if (!isHovered) setSurfaceWidth(null);
  }, [isHovered]);

  // The surface must render once before its real width is known; keyed on
  // position PRESENCE so scroll-driven repositions don't force a reflow.
  const hasPosition = position !== null;
  useLayoutEffect(() => {
    if (!isHovered || !hasPosition) return;
    const surface = surfaceRef.current;
    if (surface) setSurfaceWidth(surface.offsetWidth);
  }, [isHovered, hasPosition, label]);

  return (
    <div
      ref={anchorRef}
      className="inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isHovered &&
        position &&
        createPortal(
          <TooltipSurface
            ref={surfaceRef}
            arrowSide={position.placement === "top" ? "bottom" : "top"}
            arrowOffsetLeft={arrowOffsetLeft}
            className={clsx(
              "z-50 break-words",
              isMeasured ? "animate-fade-in" : "invisible",
            )}
            style={{
              top: `${position.top}px`,
              left: `${viewportLeft + window.scrollX}px`,
              maxWidth: `${TOOLTIP_MAX_WIDTH}px`,
              transform:
                position.placement === "top" ? "translateY(-100%)" : undefined,
            }}
          >
            {label}
          </TooltipSurface>,
          document.body,
        )}
    </div>
  );
};
